// dashboardData.js
//
// CONECTADO AOS DADOS REAIS: busca escolas/{idEscola}/dashboard/geral,
// documento calculado e mantido pelo backend (backend/dashboard.js),
// recalculado a cada mudança em alunos/professores/turmas OU relatório
// (ver comentário no topo de backend/dashboard.js sobre a correção do
// documento vindo vazio quando a escola ainda não tinha fechado nenhuma
// aula).
//
// `dashboard/geral` continua sendo só um SNAPSHOT (estado atual) — por
// isso o aproveitamento por turma abaixo usa dashboard.porTurma, que é o
// único recorte com granularidade que calcularDashboard já mantém.
//
// Já o CRESCIMENTO da escola (alunos/professores/turmas) tem série
// histórica própria, em `historicoCrescimento` (ver
// fetchHistoricoCrescimento mais abaixo e o trigger
// registrarHistoricoCrescimento em backend/dashboard.js): um ponto por
// aula fechada, com o eixo X sendo a quantidade de aulas registradas (não
// data/mês).

import { doc, getDoc, collection, getDocs } from 'firebase/firestore';
import { db } from '../../../firebase/firebase';

const VAZIO = {
  dadosEstatisticos: { numAlunos: 0, numProfessores: 0, numTurmas: 0, aproveitamentoMedioMensal: 0 },
  alunosComFalta: [],
  aproveitamentoPorTurma: [],
  porGenero: { M: 0, F: 0, naoInformado: 0 },
  porFaixaEtaria: [],
  semFaixaEtaria: 0,
  aniversariantesDoMes: [],
  atualizadoEm: null,
};

/**
 * Busca os dados reais do Dashboard no Firestore.
 * @param {string} idEscola - O ID da escola atual
 */
export const fetchDashboardData = async (idEscola) => {
  if (!idEscola) {
    return VAZIO;
  }

  const dashboardRef = doc(db, 'escolas', idEscola, 'dashboard', 'geral');
  const dashboardSnap = await getDoc(dashboardRef);

  if (!dashboardSnap.exists()) {
    // Escola nova, ainda sem nenhum aluno/professor/turma/relatório
    // cadastrado (o doc só passa a existir na primeira vez que
    // calcularDashboard roda — ver backend/dashboard.js).
    return VAZIO;
  }

  const dados = dashboardSnap.data();
  const geral = dados.geral || {};
  const porTurma = dados.porTurma || {};

  // `porTurma` vem do backend só como {idTurma: percentual}, sem nome —
  // busca os nomes das turmas pra poder rotular o gráfico.
  let nomesPorTurma = {};
  const idsTurma = Object.keys(porTurma);
  if (idsTurma.length > 0) {
    const turmasSnap = await getDocs(collection(db, 'escolas', idEscola, 'turmas'));
    turmasSnap.docs.forEach((t) => {
      nomesPorTurma[t.id] = t.data().nome;
    });
  }

  const aproveitamentoPorTurma = idsTurma.map((idTurma) => ({
    turma: nomesPorTurma[idTurma] || 'Turma',
    aproveitamento: porTurma[idTurma],
  }));

  return {
    dadosEstatisticos: {
      numAlunos: geral.numAlunos || 0,
      numProfessores: geral.numProfessores || 0,
      numTurmas: geral.numTurmas || 0,
      aproveitamentoMedioMensal: geral.aproveitamentoMedioMensal || 0,
    },
    alunosComFalta: geral.alunosComFaltasSeguidas || [],
    aproveitamentoPorTurma,
    // NOVO: perfil demográfico (só alunos) e aniversariantes do mês
    // (alunos + professores) — calculados em backend/dashboard.js.
    porGenero: geral.porGenero || { M: 0, F: 0, naoInformado: 0 },
    porFaixaEtaria: geral.porFaixaEtaria || [],
    semFaixaEtaria: geral.semFaixaEtaria || 0,
    aniversariantesDoMes: geral.aniversariantesDoMes || [],
    atualizadoEm: dados.atualizadoEm || null,
  };
};

/**
 * Busca a série histórica de crescimento (alunos/professores/turmas por
 * quantidade de aulas registradas), usada no gráfico de linhas em
 * GraficoCrescimento.js.
 *
 * Cada ponto é gravado pela Cloud Function `registrarHistoricoCrescimento`
 * (backend/dashboard.js) toda vez que uma aula é fechada (chamada
 * encerrada). Os IDs dos documentos têm zero-padding (ex.: "000012"), por
 * isso a ordenação por __name__ já sai cronológica sem precisar de índice
 * composto num campo numérico.
 *
 * Escolas novas, ou que ainda não fecharam nenhuma aula, simplesmente não
 * têm nenhum documento nesta coleção — retorna lista vazia (o componente
 * do gráfico já trata esse caso).
 *
 * @param {string} idEscola - O ID da escola atual
 */
export const fetchHistoricoCrescimento = async (idEscola) => {
  if (!idEscola) return [];

  const historicoRef = collection(db, 'escolas', idEscola, 'historicoCrescimento');
  const historicoSnap = await getDocs(historicoRef);

  return historicoSnap.docs
    .map((d) => d.data())
    .sort((a, b) => a.numAulasRegistradas - b.numAulasRegistradas);
};